import { useEffect, useState } from 'react';
import { Form } from 'antd';
import { useAuth } from '@/contexts/auth.context';
import { useAlertContext } from '@/contexts/alert.context';
import { useErrorHandler } from '@/shared/hooks/useErrorHandler';
import { profileService } from '@/services/profile.service';

export function useProfile() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [photoBase64, setPhotoBase64] = useState<string | undefined>(undefined);
  const [uploading, setUploading] = useState(false);
  const { user, updateUser } = useAuth();
  const { notify } = useAlertContext();
  const { handleError } = useErrorHandler();

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const profile = await profileService.getProfile();
      form.setFieldsValue({
        name: profile.name,
        email: profile.email,
        phoneNumber: profile.phoneNumber || '',
      });

      // Set photo base64 directly from profile
      if (profile.photoUrl) {
        setPhotoBase64(profile.photoUrl);
      } else {
        setPhotoBase64(undefined);
      }
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (file: File) => {
    setUploading(true);
    try {
      // Convert file to base64
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve) => {
        reader.onloadend = () => {
          resolve(reader.result as string);
        };
        reader.readAsDataURL(file);
      });

      const base64Image = await base64Promise;
      setPhotoBase64(base64Image);

      notify({
        type: 'success',
        message: 'Foto carregada com sucesso!',
      });
    } catch (error) {
      handleError(error);
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      const updateData: any = {
        name: values.name,
        email: values.email,
        phoneNumber: values.phoneNumber || undefined,
        photoUrl: photoBase64, // Save base64 directly
      };

      // Adicionar campos de senha apenas se fornecidos
      if (values.currentPassword && values.newPassword) {
        updateData.currentPassword = values.currentPassword;
        updateData.newPassword = values.newPassword;
      }

      await profileService.updateProfile(updateData);

      // Update user in auth context
      const userUpdateData: any = {
        name: values.name,
        email: values.email,
      };
      if (photoBase64) {
        userUpdateData.photoUrl = photoBase64;
      }
      updateUser(userUpdateData);

      notify({
        type: 'success',
        message: 'Perfil atualizado com sucesso!',
      });

      // Limpar campos de senha após sucesso
      form.setFieldsValue({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error: any) {
      // Tratamento específico para erro de senha incorreta
      const status = error?.response?.status;

      // Se for erro 500 durante atualização de senha, assumir que é senha incorreta
      // pois é o único erro de validação que lançamos como 500
      if (status === 500 && (values.currentPassword && values.newPassword)) {
        notify({
          type: 'error',
          message: 'Senha atual incorreta. Por favor, verifique e tente novamente.',
        });
        return;
      }

      // Tratamento genérico para outros erros com verificação de mensagem
      if (status === 400 || status === 500) {
        const errorData = error?.response?.data;
        const errorMessage = errorData?.errorMessage || errorData?.message || errorData?.error || '';

        if (errorMessage.includes('Current password is incorrect') ||
            errorMessage.includes('senha atual') ||
            errorMessage.includes('incorrect')) {
          notify({
            type: 'error',
            message: 'Senha atual incorreta. Por favor, verifique e tente novamente.',
          });
          return;
        }
      }

      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  return {
    form,
    user,
    loading,
    uploading,
    photoUrl: photoBase64,
    handleSubmit,
    handlePhotoUpload,
  };
}
